import React from 'react'
import { Link } from 'react-router-dom'
import { selectLimit } from '../../selectors/organizations'
import { State, Dispatch } from '../../store'
import { useSelector, useDispatch } from 'react-redux'
import { List, Box, Button, Typography } from '@mui/material'
import { fontSizes, spacing } from '../../styling'
import { humanizeDays } from '../../models/plans'
import { EventItem } from './EventItem'
import { BillingUI } from '../BillingUI'
import { Notice } from '../Notice'

export interface LogListProps {
  device?: IDevice
}

export const EventList: React.FC<LogListProps> = ({ device }) => {
  const dispatch = useDispatch<Dispatch>()
  const user = useSelector((state: State) => state.user)
  const logLimit = useSelector((state: State) => selectLimit(state, undefined, 'log-limit'))
  const { events, planUpgrade, fetching, fetchingMore } = useSelector((state: State) => state.logs)

  const fetchMore = async () => {
    await dispatch.logs.set({ after: events?.last })
    await dispatch.logs.fetch({ deviceId: device?.id })
  }

  const showPlanUpgradeNotice = Boolean(planUpgrade && !fetching && !fetchingMore)

  return (
    <>
      <List
        sx={theme => ({
          '& li': {
            padding: '4px 0',
            fontSize: fontSizes.sm,
            color: theme.palette.grayDark.main,
            alignItems: 'start',
            '& > span': {
              fontSize: fontSizes.xxs,
              textAlign: 'right',
              fontFamily: 'Roboto Mono',
              minWidth: 150,
              [theme.breakpoints.down('sm')]: { minWidth: 100, width: 100 },
            },
            '& b': { color: theme.palette.grayDarkest.main, fontWeight: 400 },
            '& i': { color: theme.palette.grayDarker.main, fontStyle: 'normal' },
            '& .event-usage': {
              display: 'flex',
              flexWrap: 'wrap',
              gap: `${spacing.xs}px`,
              marginTop: '0.6em',
              lineHeight: '0.6em',
              color: theme.palette.grayDark.main,
              fontSize: fontSizes.xxs,
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              '& > span': { display: 'inline-flex', alignItems: 'center', gap: `${spacing.xxxs}px` },
              '& svg': { flexShrink: 0 },
              '& strong': { color: theme.palette.grayDarker.main, fontWeight: 500 },
            },
          },
        })}
      >
        {events?.items?.map((item, index) => (
          <EventItem item={item} device={device} user={user} key={index} />
        ))}
      </List>
      {showPlanUpgradeNotice ? (
        <Notice
          severity="warning"
          button={
            <BillingUI>
              <Button to="/account/plans" variant="contained" color="warning" size="small" component={Link}>
                Upgrade
              </Button>
            </BillingUI>
          }
        >
          Plan upgrade required to view logs past {humanizeDays(logLimit?.value)}.
        </Notice>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            padding: `${spacing.lg}px`,
          }}
        >
          {fetching || fetchingMore ? (
            <Button color="primary" disabled size="small">
              Loading...
            </Button>
          ) : events?.hasMore ? (
            <Button color="primary" onClick={fetchMore} variant="contained" size="small">
              Load More
            </Button>
          ) : (
            <Typography variant="caption" align="center" sx={{ opacity: 0.5 }}>
              End of logs
            </Typography>
          )}
        </Box>
      )}
    </>
  )
}

